import { useRef, useState } from "react";
import { toast } from "sonner";

import { useCarrierDispatchCenters, useReassignTask } from "@/api/carrier";
import { Button } from "@/components/ui/button";
import { Dialog, type DialogProps } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";
import { hasHttpStatus } from "@/lib/api-error";

interface ReassignTaskDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  taskId: string;
  dialogPresentation?: DialogProps["presentation"];
}

/**
 * Pick a target dispatch center and POST `/tasks/:id/reassign`. The server
 * re-checks both the source task and target dispatch center before delegating
 * to the shared service.
 */
export function ReassignTaskDialog({
  open,
  onOpenChange,
  taskId,
  dialogPresentation,
}: ReassignTaskDialogProps) {
  const {
    data: dispatchCenters,
    isLoading,
    error,
  } = useCarrierDispatchCenters();
  const reassign = useReassignTask();
  const [dispatchCenterId, setDispatchCenterId] = useState<string>("");
  const [reason, setReason] = useState<string>("");
  const submitLock = useRef(false);
  const responsive = dialogPresentation === "responsive-sheet";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!dispatchCenterId || submitLock.current) return;
    submitLock.current = true;
    try {
      await reassign.mutateAsync({
        taskId,
        dispatchCenterId,
        ...(reason.trim() ? { reason: reason.trim() } : {}),
      });
      toast.success("Task reassigned.");
      onOpenChange(false);
      setDispatchCenterId("");
      setReason("");
    } catch {
      toast.error("Failed to reassign task.");
      // also surfaced inline below
    } finally {
      submitLock.current = false;
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!reassign.isPending) onOpenChange(nextOpen);
      }}
      title="Reassign Task"
      presentation={dialogPresentation}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label
            htmlFor="dispatch-center-picker"
            className="block text-sm font-medium mb-1"
          >
            Target dispatch center
          </label>
          <NativeSelect
            id="dispatch-center-picker"
            value={dispatchCenterId}
            onChange={(e) => setDispatchCenterId(e.target.value)}
            disabled={isLoading || reassign.isPending}
          >
            <option value="">Select dispatch center...</option>
            {dispatchCenters?.map((center) => (
              <option key={center.id} value={center.id}>
                {center.name}
              </option>
            ))}
          </NativeSelect>
        </div>

        <div>
          <label
            htmlFor="reason-input"
            className="block text-sm font-medium mb-1"
          >
            Reason <span className="text-muted-foreground">(optional)</span>
          </label>
          <Input
            id="reason-input"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={reassign.isPending}
          />
        </div>

        {error && (
          <p role="alert" className="text-sm text-destructive">
            {hasHttpStatus(error, 403)
              ? "You do not have permission to load Dispatch Centers."
              : "Failed to load Dispatch Centers. Try again."}
          </p>
        )}

        {reassign.isError && (
          <p role="alert" className="text-sm text-destructive">
            Failed to reassign task. Try again.
          </p>
        )}

        <div
          className={
            responsive
              ? "sticky bottom-0 grid grid-cols-2 gap-2 border-t bg-background pt-4"
              : "flex justify-end gap-2"
          }
        >
          <Button
            type="button"
            variant="outline"
            size={responsive ? "touch" : "default"}
            onClick={() => onOpenChange(false)}
            disabled={reassign.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            size={responsive ? "touch" : "default"}
            disabled={!dispatchCenterId || reassign.isPending || !!error}
          >
            {reassign.isPending ? "Reassigning..." : "Reassign"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
